import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '../../components/Navigation';
import { apiGet } from '../../utils/api';
import {
  BarChart3,
  Users,
  Globe,
  Eye,
  Clock,
  Smartphone,
  RefreshCw,
} from '../../components/Icons';

// Simple per day chart drawn with inline SVG. No chart library is installed
// and Dockerfile.ui runs npm ci --only=production, so adding one would break
// the image build.
const DailyChart = ({ series }) => {
  const points = series || [];
  if (points.length === 0) {
    return <div className="h-64 flex items-center justify-center text-gray-500">No views recorded yet</div>;
  }

  const width = 900;
  const height = 240;
  const padLeft = 44;
  const padRight = 12;
  const padTop = 12;
  const padBottom = 34;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;

  const maxViews = Math.max(1, ...points.map((p) => p.views));
  const barSlot = plotW / points.length;
  const barW = Math.max(2, Math.min(barSlot * 0.7, 40));

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((frac) => ({
    frac,
    value: Math.round(maxViews * frac),
    y: padTop + plotH - plotH * frac,
  }));

  // Label at most 8 dates so the axis stays readable on any range.
  const labelEvery = Math.max(1, Math.ceil(points.length / 8));

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: '520px' }} role="img" aria-label="Page views per day">
        {gridLines.map((line) => (
          <g key={line.frac}>
            <line x1={padLeft} y1={line.y} x2={width - padRight} y2={line.y} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padLeft - 8} y={line.y + 4} textAnchor="end" fontSize="11" fill="#6b7280">
              {line.value}
            </text>
          </g>
        ))}

        {points.map((point, index) => {
          const barH = (point.views / maxViews) * plotH;
          const x = padLeft + index * barSlot + (barSlot - barW) / 2;
          const y = padTop + plotH - barH;
          const sessionH = (point.sessions / maxViews) * plotH;
          return (
            <g key={point.date}>
              <rect x={x} y={y} width={barW} height={Math.max(barH, point.views > 0 ? 2 : 0)} fill="#2563eb" rx="2">
                <title>{`${point.date}: ${point.views} views, ${point.sessions} sessions`}</title>
              </rect>
              <rect
                x={x}
                y={padTop + plotH - sessionH}
                width={barW}
                height={Math.max(sessionH, point.sessions > 0 ? 2 : 0)}
                fill="#93c5fd"
                rx="2"
                opacity="0.85"
              >
                <title>{`${point.date}: ${point.sessions} sessions`}</title>
              </rect>
              {index % labelEvery === 0 && (
                <text
                  x={x + barW / 2}
                  y={height - 12}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#6b7280"
                >
                  {point.date.slice(5)}
                </text>
              )}
            </g>
          );
        })}

        <line x1={padLeft} y1={padTop + plotH} x2={width - padRight} y2={padTop + plotH} stroke="#9ca3af" strokeWidth="1" />
      </svg>
      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600">
        <span className="flex items-center"><span className="inline-block w-3 h-3 rounded-sm mr-1" style={{ background: '#2563eb' }} /> Views</span>
        <span className="flex items-center"><span className="inline-block w-3 h-3 rounded-sm mr-1" style={{ background: '#93c5fd' }} /> Sessions</span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center">
      <div className={`p-3 rounded-full ${tone}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const formatDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  const rest = total % 60;
  return `${mins}m ${rest}s`;
};

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
};

const SiteAnalytics = () => {
  const [days, setDays] = useState('30');
  const [summary, setSummary] = useState(null);
  const [pages, setPages] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sessionDays = Math.min(parseInt(days, 10) || 7, 90);
      const [summaryRes, pagesRes, referrersRes, sessionsRes] = await Promise.all([
        apiGet('/api/analytics/summary', { days }),
        apiGet('/api/analytics/pages', { days, limit: 50 }),
        apiGet('/api/analytics/referrers', { days, limit: 25 }),
        apiGet('/api/analytics/sessions', { days: sessionDays, limit: 50 }),
      ]);

      if (summaryRes.ok) setSummary(summaryRes.data);
      else setError('Could not load the summary');

      if (pagesRes.ok) setPages(pagesRes.data.pages || []);
      if (referrersRes.ok) setReferrers(referrersRes.data.referrers || []);
      if (sessionsRes.ok) setSessions(sessionsRes.data.sessions || []);
    } catch (err) {
      console.error('Error loading site analytics:', err);
      setError('Could not load site analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const topDevice = summary && summary.devices && summary.devices.length > 0
    ? summary.devices[0].device
    : 'none';

  if (loading && !summary) {
    return (
      <Navigation>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading site analytics...</p>
          </div>
        </div>
      </Navigation>
    );
  }

  return (
    <Navigation>
      <div className="bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Site Analytics</h1>
                <p className="text-sm text-gray-600">First party traffic for the public marketing pages</p>
              </div>
              <div className="flex items-center space-x-3">
                <select
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                </select>
                <button
                  onClick={load}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard icon={Eye} label="Total Views" value={summary?.total_views ?? 0} tone="bg-blue-100 text-blue-600" />
            <StatCard icon={Users} label="Unique Sessions" value={summary?.unique_sessions ?? 0} tone="bg-green-100 text-green-600" />
            <StatCard icon={BarChart3} label="Views per Session" value={summary?.views_per_session ?? 0} tone="bg-purple-100 text-purple-600" />
            <StatCard icon={Smartphone} label="Top Device" value={topDevice} tone="bg-yellow-100 text-yellow-600" />
          </div>

          {/* Per day chart */}
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Views per Day</h3>
            </div>
            <div className="p-6">
              <DailyChart series={summary?.series} />
            </div>
          </div>

          {/* Pages and referrers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">Top Pages</h4>
                <BarChart3 className="h-5 w-5 text-gray-400" />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Path</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pages.length === 0 && (
                      <tr><td colSpan="3" className="px-6 py-6 text-sm text-gray-500 text-center">No page views yet</td></tr>
                    )}
                    {pages.map((page) => (
                      <tr key={page.path} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm text-gray-900 font-mono break-all">{page.path}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right font-medium">{page.views}</td>
                        <td className="px-6 py-3 text-sm text-gray-600 text-right">{page.sessions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-lg font-medium text-gray-900">Referrers</h4>
                <Globe className="h-5 w-5 text-gray-400" />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Sessions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {referrers.length === 0 && (
                      <tr><td colSpan="3" className="px-6 py-6 text-sm text-gray-500 text-center">No referrers yet</td></tr>
                    )}
                    {referrers.map((ref) => (
                      <tr key={ref.source} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm text-gray-900 break-all">{ref.source}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right font-medium">{ref.views}</td>
                        <td className="px-6 py-3 text-sm text-gray-600 text-right">{ref.sessions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Recent sessions */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">Recent Sessions</h4>
              <Clock className="h-5 w-5 text-gray-400" />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exit</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Pages</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Journey</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.length === 0 && (
                    <tr><td colSpan="8" className="px-6 py-6 text-sm text-gray-500 text-center">No sessions yet</td></tr>
                  )}
                  {sessions.map((session) => (
                    <React.Fragment key={session.session_id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{formatWhen(session.started_at)}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 font-mono">{session.entry_page}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 font-mono">{session.exit_page}</td>
                        <td className="px-6 py-3 text-sm text-gray-900 text-right font-medium">{session.page_count}</td>
                        <td className="px-6 py-3 text-sm text-gray-600 text-right whitespace-nowrap">{formatDuration(session.duration_seconds)}</td>
                        <td className="px-6 py-3 text-sm text-gray-600 break-all">{session.referrer_host}</td>
                        <td className="px-6 py-3 text-sm text-gray-600">
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{session.device_type}</span>
                          {session.country && <span className="ml-2 text-xs text-gray-500">{session.country}</span>}
                        </td>
                        <td className="px-6 py-3 text-sm text-right">
                          <button
                            onClick={() => setExpanded(expanded === session.session_id ? null : session.session_id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {expanded === session.session_id ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {expanded === session.session_id && (
                        <tr className="bg-gray-50">
                          <td colSpan="8" className="px-6 py-4">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Page sequence</p>
                            <ol className="space-y-1">
                              {session.pages.map((page, index) => (
                                <li key={`${session.session_id}-${index}`} className="text-sm text-gray-700 flex items-center">
                                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs mr-3">{index + 1}</span>
                                  <span className="font-mono">{page.path}</span>
                                  <span className="ml-3 text-xs text-gray-500">{formatWhen(page.at)}</span>
                                </li>
                              ))}
                            </ol>
                            {session.referrer && (
                              <p className="mt-3 text-xs text-gray-500 break-all">Full referrer: {session.referrer}</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-6 text-xs text-gray-500">
            Collected first party. No cookies, no IP addresses, no personal data. Session ids live in the
            visitor's sessionStorage and disappear when the tab closes. Known bots are excluded from these figures.
          </p>
        </div>
      </div>
    </Navigation>
  );
};

export default SiteAnalytics;
